import {
  hideCredentialDialog,
  credentialDialogSaved,
} from '../store/actions/credentialDialog.actions';
import usePermissions from 'features/shared/hooks/usePermissions';
import { PERMISSION_RESOURCES } from 'features/shared/constants/permissionResources';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, FormProvider, useWatch, useFormState } from 'react-hook-form';
import { Grid } from '@material-ui/core';
import RMSCommonDialog from '../../shared/components/dialog/RMSCommonDialog';
import { RMSConfirmationDialog } from '../../shared/components/dialog';
import {
  RMSTextField,
  RMSSelect,
  RMSCheckbox,
  RMSDatePicker,
} from '../../shared/components/form-controls';
import RMSRecordHistoryButton from '../../shared/components/modules/RMSRecordHistoryButton';
import SearchPlaceAutoComplete from '../../shared/components/modules/SearchPlaceAutoComplete';
import SSNTextMask from 'components/RMSTextMask/SSNTextMask';
import CreateFormResolver from '../../shared/utility/createFormResolver';
import { executeApiAction } from '../../shared/utility/apiHelper';
import { notify } from 'reducers/NotifierReducer';
import useConfirmDialog from '../../shared/utility/useConfirmDialog';
import personCredentialSchema from '../schema/personCredentialSchema';
import { getDictionarySelectProps } from 'features/shared/utility/dictionarySelectHelper';
import { DEFAULT_VALUES, CREDENTIAL_TYPE } from '../constants/personCredentialFormConstants';
import useUserDictionaryOptions from '../hooks/useUserDictionaryOptions';
import {
  createPersonCredential,
  editPersonCredential,
  getPersonCredential,
} from '../services/credentialService';

const AddEditCredentialDialog = ({ dialog }) => {
  const { personId, credentialId = null, requestId } = dialog;
  const open = true;
  const dispatch = useDispatch();
  const onClose = useCallback(() => dispatch(hideCredentialDialog(requestId)), [
    dispatch,
    requestId,
  ]);
  const onSaved = useCallback(
    (credential) => {
      dispatch(credentialDialogSaved(credential, requestId));
      dispatch(hideCredentialDialog(requestId));
    },
    [dispatch, requestId]
  );
  const currentUser = useSelector((state) => state.user.userData.user);
  const [submitting, setSubmitting] = useState(false);
  const isEditMode = Boolean(credentialId);
  const [credential, setCredential] = useState(null);
  const [loading, setLoading] = useState(isEditMode);
  const canEdit = usePermissions(PERMISSION_RESOURCES.PERSON).Edit;
  const canSave =
    canEdit && !loading && (!isEditMode || credential?.ptsCredentialID === credentialId);

  const methods = useForm({
    resolver: CreateFormResolver(personCredentialSchema),
    mode: 'onSubmit',
    disabled: !canSave,
    defaultValues: { ...DEFAULT_VALUES },
  });

  const { reset } = methods;

  const { isDirty } = useFormState({ control: methods.control });
  const { confirmState, requestConfirm, handleYes, handleNo } = useConfirmDialog();
  const {
    credentialTypeOptions,
    credentialClassOptions,
    stateOptions,
  } = useUserDictionaryOptions();
  const credentialType = useWatch({ control: methods.control, name: 'Type' });
  const ptsIssuedByID = useWatch({ control: methods.control, name: 'ptsIssuedByID' });

  const handlePlaceCreated = useCallback(
    (place) => {
      methods.setValue('ptsIssuedByID', place?.ptsPlaceID ?? null, {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
    [methods]
  );

  const onSubmit = useCallback(
    async (data) => {
      if (!canSave) return;
      const payload = {
        Type: data.Type || null,
        Number: data.Number,
        State: data.State || null,
        Class: data.Class || null,
        IsCommercial: data.IsCommercial || false,
        ptsIssuedByID: data.ptsIssuedByID || null,
        Issued: data.Issued || null,
        Expiration: data.Expiration || null,
        Description: data.Description || null,
        Notes: data.Notes || null,
        UpdatedBy: currentUser?.Username,
      };

      if (!isEditMode) {
        payload.ptsPersonID = personId;
        payload.CreatedBy = currentUser?.Username;
      }

      await executeApiAction({
        setLoading: setSubmitting,
        dispatch,
        action: () =>
          isEditMode
            ? editPersonCredential(credentialId, payload)
            : createPersonCredential(payload),
        onSuccess: (savedCredential) => {
          dispatch(
            notify(
              isEditMode ? 'Credential updated successfully' : 'Credential created successfully',
              'success'
            )
          );
          onSaved?.(savedCredential);
        },
      });
    },
    [isEditMode, credentialId, personId, currentUser, dispatch, onSaved, canSave]
  );

  const handleSave = methods.handleSubmit(onSubmit);

  const requestClose = useCallback(() => {
    if (isDirty) {
      requestConfirm(
        'You have unsaved changes on this credential. Are you sure you want to close without saving?',
        () => onClose?.(),
        'Unsaved Changes'
      );
      return;
    }

    onClose?.();
  }, [isDirty, requestConfirm, onClose]);

  useEffect(() => {
    if (!open) return;
    let active = true;
    setCredential(null);
    reset({ ...DEFAULT_VALUES });
    if (!credentialId) {
      setLoading(false);
      return;
    }
    executeApiAction({
      dispatch,
      setLoading: (value) => {
        if (active) setLoading(value);
      },
      action: () => getPersonCredential(credentialId),
      onSuccess: (record) => {
        if (!active || !record?.ptsCredentialID) return;
        reset({ ...DEFAULT_VALUES, ...record });
        setCredential(record);
      },
    });
    return () => {
      active = false;
    };
  }, [open, credentialId, dispatch, reset]);

  return (
    <RMSCommonDialog
      open={open}
      title={isEditMode ? (canEdit ? 'Edit Credential' : 'View Credential') : 'Add Credential'}
      onClose={requestClose}
      onSave={handleSave}
      loading={submitting || loading}
      saveDisabled={submitting || !canSave}
      maxWidth="sm"
      fullWidth
      closeOnBackdropClick={false}
      saveButton={{
        show: true,
        type: 'simple',
        label: 'Save',
        variant: 'contained',
        color: 'primary',
      }}
      cancelButton={{
        show: true,
        type: 'simple',
        label: 'Close',
        variant: 'outlined',
        color: 'secondary',
      }}
      leftActions={
        isEditMode && (
          <RMSRecordHistoryButton
            disabled={!credential?.Created}
            created={credential?.Created}
            createdBy={credential?.CreatedBy}
            updated={credential?.Updated}
            updatedBy={credential?.UpdatedBy}
          />
        )
      }>
      <FormProvider {...methods}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <RMSSelect
              name="Type"
              label="Type"
              options={credentialTypeOptions}
              {...getDictionarySelectProps()}
            />
          </Grid>
          <Grid item xs={6}>
            <RMSTextField
              name="Number"
              label="Number"
              disabled={!canSave || !credentialType}
              inputProps={{ maxLength: 50 }}
              InputProps={
                credentialType === CREDENTIAL_TYPE.SSN ? { inputComponent: SSNTextMask } : undefined
              }
            />
          </Grid>
          <Grid item xs={6}>
            <RMSSelect
              name="State"
              label="State"
              options={stateOptions}
              {...getDictionarySelectProps()}
            />
          </Grid>
          <Grid item xs={6}>
            <RMSSelect
              name="Class"
              label="Class"
              options={credentialClassOptions}
              {...getDictionarySelectProps()}
            />
          </Grid>
          <Grid item xs={6}>
            <RMSCheckbox name="IsCommercial" label="Commercial" />
          </Grid>
          <Grid item xs={6}>
            <SearchPlaceAutoComplete
              name="ptsIssuedByID"
              label="Issued By"
              defaultPlaceId={ptsIssuedByID}
              onPlaceCreated={handlePlaceCreated}
              onPlaceUpdated={handlePlaceCreated}
              disabled={!canSave}
              showDetailsIcon={false}
            />
          </Grid>
          <Grid item xs={6}>
            <RMSDatePicker name="Issued" label="Issued Date" />
          </Grid>
          <Grid item xs={6}>
            <RMSDatePicker name="Expiration" label="Expiration Date" />
          </Grid>
          <Grid item xs={12}>
            <RMSTextField name="Description" label="Description" inputProps={{ maxLength: 2000 }} />
          </Grid>
          <Grid item xs={12}>
            <RMSTextField
              name="Notes"
              label="Notes"
              multiline
              rows={3}
              inputProps={{ maxLength: 2000 }}
            />
          </Grid>
        </Grid>
      </FormProvider>

      {confirmState.open && (
        <RMSConfirmationDialog
          open={confirmState.open}
          title={confirmState.title}
          message={confirmState.message}
          onNo={handleNo}
          onYes={handleYes}
        />
      )}
    </RMSCommonDialog>
  );
};

export default AddEditCredentialDialog;
